import { useRef } from 'react';

import { appStrings } from '@backrs_ui/common-ui';

import { Box, Button, Theme, useMediaQuery } from '@mui/material';

import { footerSx, actionsSx } from './CommandPalette.styles';
import { CommandPaletteProps } from './CommandPalette.types';
import { CommandPaletteBase } from './CommandPaletteBase';
import { CommandPaletteList } from './CommandPaletteList';
import { COMMAND_PALETTE_TITLE_ID, CommandPaletteSearch } from './CommandPaletteSearch';
import { ControllerHints } from './ControllerHints';

export const CommandPalette = <T,>({
  open,
  onClose,
  title,
  searchPlaceholder = 'Search...',
  searchValue,
  onSearchChange,
  items,
  renderItem,
  getItemKey,
  selectedItem,
  onItemSelect,
  sections,
  actions,
  footerContent,
  loading = false,
  emptyStateMessage = appStrings.commandPalette.noResultsFound,
}: CommandPaletteProps<T>) => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const keyboardHandlerRef = useRef<((event: React.KeyboardEvent) => void) | null>(null);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (keyboardHandlerRef.current) {
      keyboardHandlerRef.current(event);
    }
  };

  const setKeyboardHandler = (handler: (event: React.KeyboardEvent) => void) => {
    keyboardHandlerRef.current = handler;
  };

  if (!open) return null;

  return (
    <CommandPaletteBase
      open={open}
      onClose={onClose}
      onKeyDown={handleKeyDown}
      labelledBy={title ? COMMAND_PALETTE_TITLE_ID : undefined}
    >
      <CommandPaletteSearch
        title={title}
        searchPlaceholder={searchPlaceholder}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        onClose={onClose}
      />

      <CommandPaletteList
        items={items}
        sections={sections}
        renderItem={renderItem}
        getItemKey={getItemKey}
        selectedItem={selectedItem}
        onItemSelect={onItemSelect}
        onClose={onClose}
        loading={loading}
        emptyStateMessage={emptyStateMessage}
        onKeyDown={setKeyboardHandler}
      />

      {(footerContent || actions) && (
        <Box sx={footerSx}>
          {footerContent}
          {actions && (
            <Box sx={actionsSx}>
              {actions.map((action, index) => (
                <Button
                  id={action.id ?? `command-palette-action-${index}`}
                  key={index}
                  onClick={() => action.onClick(selectedItem)}
                  disabled={action.disabled}
                  startIcon={action.icon}
                  size="md"
                  hierarchy={
                    action.hierarchy ??
                    (selectedItem && !action.disabled ? 'Primary' : 'Secondary gray')
                  }
                  variant={action.variant || 'outlined'}
                >
                  {action.label}
                </Button>
              ))}
            </Box>
          )}
          {!isMobile && (
            <ControllerHints
              hints={[
                {
                  keys: ['↑', '↓'],
                  label: appStrings.commandPalette.navigateHint,
                  keyWeight: 'bold',
                },
                { keys: ['↵'], label: appStrings.commandPalette.selectHint, keyWeight: 'normal' },
                { keys: ['esc'], label: appStrings.commandPalette.closeHint, keyWeight: 'normal' },
              ]}
            />
          )}
        </Box>
      )}
    </CommandPaletteBase>
  );
};
